import React, { useState } from "react";
import SVG from "react-inlinesvg";
import { toAbsoluteUrl } from "../../../../_metronic/_helpers";
import "../SortDropdown/SortDropdownCell.scss";

// Helper function to get nested object values
const getNestedValue = (obj, path) => {
  return path.split(".").reduce((current, key) => {
    return current && current[key] !== undefined ? current[key] : null;
  }, obj);
};

// Utility function to sort array of objects
export const sortData = (data, sortConfig, sortOptions = {}) => {
  if (!sortConfig || !sortConfig.column || !sortConfig.direction) {
    return data;
  }

  const { column, direction } = sortConfig;

  return [...data].sort((a, b) => {
    let aVal, bVal;

    // Check if there's a custom sort function for this column
    if (sortOptions[column] && typeof sortOptions[column] === "function") {
      aVal = sortOptions[column](a);
      bVal = sortOptions[column](b);
    } else {
      // Default: get value by column key (supports nested properties)
      aVal = getNestedValue(a, column);
      bVal = getNestedValue(b, column);
    }

    // Handle null/undefined values
    if (aVal == null && bVal == null) return 0;
    if (aVal == null) return direction === "asc" ? 1 : -1;
    if (bVal == null) return direction === "asc" ? -1 : 1;

    // Convert to string for comparison if needed
    if (typeof aVal === "string") aVal = aVal.toLowerCase();
    if (typeof bVal === "string") bVal = bVal.toLowerCase();

    if (aVal < bVal) return direction === "asc" ? -1 : 1;
    if (aVal > bVal) return direction === "asc" ? 1 : -1;
    return 0;
  });
};

// Main SortDropdownHeader Component
const SortDropdownHeader = ({
  columnKey,
  onSort,
  currentSort,
  sortable = true,
  children,
  align = "left",
}) => {
  const [open, setOpen] = useState(false);

  if (!sortable) return <>{children}</>;

  const currentSortDirection =
    currentSort?.column === columnKey ? currentSort.direction : null;

  const handleSort = (direction) => {
    onSort(columnKey, direction);
    setOpen(false);
  };

  const clearSort = () => {
    onSort(null, null);
    setOpen(false);
  };

  return (
    <div className="sort-dropdown-wrapper w-100 d-inline-flex gap-2 align-items-center justify-content-between">
      <span className="me-2">
        {children}
        {currentSortDirection && (
          <span className="position-absolute ms-1">
            <SVG
              height={14}
              width={14}
              src={toAbsoluteUrl(
                currentSortDirection === "desc"
                  ? "media/svg/icons/General/arrow-down.svg"
                  : "media/svg/icons/General/arrow-up.svg"
              )}
            />
          </span>
        )}
      </span>

      <div
        className="position-relative"
        tabIndex={0}
        onBlur={(e) => {
          if (!e.currentTarget.contains(e.relatedTarget)) {
            setOpen(false);
          }
        }}>
        <button
          type="button"
          className="sort-dropdown-button font-size-sm"
          onClick={() => setOpen(!open)}>
          <i className="fas fa-ellipsis-v" />
        </button>

        {open && (
          <>
            {/* Backdrop to close dropdown */}
            <div
              className="sort-dropdown-backdrop position-fixed"
              style={{ top: 0, left: 0, right: 0, bottom: 0, zIndex: 999 }}
              onClick={() => setOpen(false)}
            />

            <div
              className={`sort-dropdown-list position-absolute bg-white border mt-1 ${
                align === "right" ? "align-right" : ""
              }`}>
              {/* Ascending */}
              {currentSortDirection !== "asc" && (
                <button
                  className="dropdown-item w-100 text-start"
                  onClick={() => handleSort("asc")}>
                  <span className="me-2">
                    <SVG
                      height={16}
                      width={16}
                      src={toAbsoluteUrl(
                        "media/svg/icons/General/arrow-up.svg"
                      )}
                    />
                  </span>
                  Sort Ascending
                </button>
              )}

              {/* Descending */}
              {currentSortDirection !== "desc" && (
                <button
                  className="dropdown-item w-100 text-start"
                  onClick={() => handleSort("desc")}>
                  <span className="me-2">
                    <SVG
                      height={16}
                      width={16}
                      src={toAbsoluteUrl(
                        "media/svg/icons/General/arrow-down.svg"
                      )}
                    />
                  </span>
                  Sort Descending
                </button>
              )}

              {/* Clear */}
              {currentSortDirection && (
                <button
                  className="dropdown-item w-100 text-start text-black"
                  onClick={clearSort}>
                  <span className="me-2">
                    <SVG
                      height={16}
                      width={16}
                      src={toAbsoluteUrl(
                        "media/svg/icons/General/clear-sort.svg"
                      )}
                    />
                  </span>
                  Clear Sort
                </button>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default SortDropdownHeader;
